import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Info, TrendingUp, Target } from 'lucide-react';
import { useDashboardTranslations } from '@/hooks/useDashboardTranslations';
import { useTheme } from '@/contexts/ThemeContext';

interface MissingField {
  attribute_label: string;
  missing_count: number;
}

interface MostMissingAttributesCardProps {
  mostMissingFields: MissingField[];
  loading: boolean;
  className?: string;
}

export function MostMissingAttributesCard({
  mostMissingFields,
  loading,
  className,
}: MostMissingAttributesCardProps) {
  const i18n = useDashboardTranslations();
  const { actualTheme } = useTheme();

  // Prepare top 5 fields to match height with completeness trend
  const topFields = (mostMissingFields || []).slice(0, 5);
  const maxMissing = topFields.length > 0 ? Math.max(...topFields.map(f => f.missing_count)) : 0;
  const totalMissing = topFields.reduce((sum, f) => sum + f.missing_count, 0);

  // Calculate severity levels
  const getSeverityLevel = (count: number) => {
    const percentage = maxMissing > 0 ? (count / maxMissing) * 100 : 0;
    if (percentage >= 80) return 'critical';
    if (percentage >= 60) return 'high';
    if (percentage >= 40) return 'medium';
    return 'low';
  };

  const getSeverityColor = (level: string) => {
    switch (level) {
      case 'critical':
        return actualTheme === 'dark' ? '#ef4444' : '#ef4444'; // Keep red for critical
      case 'high':
        return actualTheme === 'dark' ? '#f97316' : '#f97316'; // Keep orange for high
      case 'medium':
        return actualTheme === 'dark' ? '#eab308' : '#eab308'; // Keep yellow for medium
      case 'low':
        return actualTheme === 'dark' ? '#60a5fa' : '#3b82f6'; // Light blue for dark mode
      default:
        return actualTheme === 'dark' ? '#6b7280' : '#6b7280';
    }
  };

  const getSeverityBadgeVariant = (level: string) => {
    switch (level) {
      case 'critical':
        return 'destructive';
      case 'high':
        return 'default';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'outline';
    }
  };

  return (
    <Card className={`h-full ${className || ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <AlertTriangle className="size-5 text-orange-500 dark:text-orange-400" />
            {i18n.getMostMissingFieldsText('title')}
          </CardTitle>
          {!loading && topFields.length > 0 && (
            <Badge variant="outline" className="text-xs dark:border-border dark:text-foreground">
              {i18n.getMostMissingFieldsText('fields', {
                count: topFields.length,
              })}
            </Badge>
          )}
        </div>
        {!loading && topFields.length > 0 && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Target className="size-4" />
              <span>
                {i18n.getMostMissingFieldsText('total', {
                  count: totalMissing.toLocaleString(),
                })}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingUp className="size-4" />
              <span>
                {i18n.getMostMissingFieldsText('max', {
                  count: maxMissing.toLocaleString(),
                })}
              </span>
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent className="h-[calc(100%-120px)] overflow-hidden">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-12" />
                </div>
                <Skeleton className="h-1.5 w-full" />
              </div>
            ))}
          </div>
        ) : topFields.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center py-6 text-center">
            <div
              className={`mb-3 flex size-12 items-center justify-center rounded-full ${
                actualTheme === 'dark' ? 'bg-green-900/20' : 'bg-green-100'
              }`}
            >
              <Info
                className={`size-6 ${actualTheme === 'dark' ? 'text-green-400' : 'text-green-600'}`}
              />
            </div>
            <h3
              className={`mb-1 font-medium ${
                actualTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}
            >
              {i18n.getMostMissingFieldsText('noMissingTitle')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {i18n.getMostMissingFieldsText('noMissingDesc')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {topFields.map((field: MissingField, index: number) => {
              const percentage = maxMissing > 0 ? (field.missing_count / maxMissing) * 100 : 0;
              const severity = getSeverityLevel(field.missing_count);

              return (
                <div
                  key={field.attribute_label}
                  className={`group rounded-lg border p-2 transition-all duration-200 ${
                    actualTheme === 'dark'
                      ? 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span
                        className={`w-5 text-xs font-medium ${
                          actualTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}
                      >
                        #{index + 1}
                      </span>
                      <span
                        className={`truncate text-sm font-medium ${
                          actualTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                        }`}
                        title={field.attribute_label}
                      >
                        {field.attribute_label}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge
                        variant={getSeverityBadgeVariant(severity)}
                        className="px-1.5 py-0.5 text-xs capitalize"
                      >
                        {i18n.getSeverityText(severity)}
                      </Badge>
                      <span
                        className={`text-sm font-semibold ${
                          actualTheme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                        }`}
                      >
                        {field.missing_count.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Progress
                      value={percentage}
                      className="h-1.5"
                      style={
                        {
                          '--progress-background': getSeverityColor(severity),
                        } as React.CSSProperties
                      }
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>
                        {i18n.getMostMissingFieldsText('percentOfMax', {
                          percent: percentage.toFixed(1),
                        })}
                      </span>
                      <span>
                        {i18n.getMostMissingFieldsText('percentOfTotal', {
                          percent: ((field.missing_count / totalMissing) * 100).toFixed(1),
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
